import React, { useMemo, useState } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const INDUSTRIES = [
  'Software / SaaS', 'Agency / Consulting', 'E-commerce / Retail', 'Real Estate',
  'Finance / Insurance', 'Healthcare', 'Manufacturing', 'Construction / Trades',
  'Education', 'Hospitality', 'Logistics', 'Other',
];

// Step 1 — the facts every agent is born knowing (World Card) plus the
// business timezone, which drives when agents consider themselves on the
// clock (office hours, routines, "9 AM" meaning YOUR 9 AM).
const StepBasics = ({ status, showNotification, onDone }) => {
  const detectedTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
    } catch {
      return 'America/Toronto';
    }
  }, []);
  const timezones = useMemo(() => {
    try {
      return Intl.supportedValuesOf('timeZone');
    } catch {
      return [detectedTz, 'America/Toronto', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/London', 'UTC'];
    }
  }, [detectedTz]);

  const [form, setForm] = useState({
    businessName: status.businessName === 'My Business' ? '' : (status.businessName || ''),
    industry: status.industry || '',
    website: status.website || '',
    timezone: status.timezone || detectedTz,
    whatYouSell: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.businessName.trim().length >= 2 && form.whatYouSell.trim().length >= 10;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await axiosInstance.post('/api/business/onboarding/basics', {
        businessName: form.businessName.trim(),
        industry: form.industry || undefined,
        website: form.website.trim() || undefined,
        timezone: form.timezone,
        whatYouSell: form.whatYouSell.trim(),
      });
      onDone({ website: form.website.trim(), whatYouSell: form.whatYouSell.trim() });
    } catch (error) {
      logger.error('Onboarding basics failed:', error);
      showNotification?.(error.response?.data?.message || 'Could not save your details', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500';

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Make this workspace yours</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Your AI employee is born knowing these facts — they shape every email it writes and every call it takes.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Workspace name *</label>
          <input className={inputCls} value={form.businessName} onChange={set('businessName')} placeholder="Your name, team, or business" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Industry</label>
            <select className={inputCls} value={form.industry} onChange={set('industry')}>
              <option value="">Select…</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timezone *</label>
            <select className={inputCls} value={form.timezone} onChange={set('timezone')}>
              {timezones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Your agent works your office hours in this timezone.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
          <input className={inputCls} value={form.website} onChange={set('website')} placeholder="https://acme.com" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            What should your agent help with? *
          </label>
          <textarea
            className={`${inputCls} min-h-[90px]`}
            value={form.whatYouSell}
            onChange={set('whatYouSell')}
            placeholder="I run a design studio. Help me respond to enquiries, prepare proposals, and follow up with clients."
          />
          <p className="text-xs text-gray-400 mt-1">Plain language is perfect — this becomes your agent's permanent memory.</p>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={submit}
          disabled={!valid || saving}
          className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium"
        >
          {saving ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default StepBasics;
